<template>
    <div v-if="summary" class="sc-card" :class="{ 'sc-card--collapsed': collapsed }">
        <div class="sc-header" @click="$emit('toggle')">
            <div class="sc-header-left">
                <span class="sc-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/>
                        <path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8V6Z"/>
                    </svg>
                </span>
                <span class="sc-label">AI Summary</span>
            </div>
            <div class="sc-meta">
                <span class="sc-date">{{ summary.date }}</span>
                <span class="sc-count">{{ summary.totalArticles }} articles</span>
                <span class="sc-toggle" :class="{ 'sc-toggle--collapsed': collapsed }">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <polyline points="6 9 12 15 18 9"/>
                    </svg>
                </span>
            </div>
        </div>
        <div class="sc-body" v-show="!collapsed">
        <p class="sc-overview">{{ summary.overview }}</p>
        <div class="sc-topics" v-if="summary.topics && summary.topics.length">
            <button
                v-for="topic in summary.topics"
                :key="topic.name"
                class="sc-topic"
                :class="'sc-topic--' + topic.sentiment"
                @click="$emit('select-topic', topic.name)"
            >
                <span class="sc-topic-name">{{ topic.name }}</span>
                <span class="sc-topic-sentiment">{{ sentimentLabel(topic.sentiment) }}</span>
            </button>
        </div>
        <div class="sc-details" v-if="summary.topics && summary.topics.length">
            <div v-for="topic in summary.topics" :key="topic.name" class="sc-topic-detail">
                <div class="sc-topic-detail-header">
                    <span class="sc-topic-detail-name">{{ topic.name }}</span>
                    <span class="sc-topic-detail-badge" :class="'sc-topic--' + topic.sentiment">
                        {{ sentimentLabel(topic.sentiment) }}
                    </span>
                </div>
                <p class="sc-topic-detail-summary">{{ topic.summary }}</p>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SummaryCard',
    props: {
        summary: { type: Object, default: null },
        collapsed: { type: Boolean, default: false },
    },
    emits: ['select-topic', 'toggle'],
    methods: {
        sentimentLabel(s) {
            const map = { positive: 'Positive', neutral: 'Neutral', negative: 'Negative' };
            return map[s] || s;
        },
    },
};
</script>

<style scoped>
.sc-card {
    background: var(--bg-card);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-md);
    border: 1px solid var(--border-light);
    padding: 16px 20px;
    margin-bottom: 12px;
}

.sc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    cursor: pointer;
    user-select: none;
}

.sc-card--collapsed .sc-header {
    margin-bottom: 0;
}

.sc-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.sc-icon {
    display: flex;
    align-items: center;
    color: var(--color-primary);
}

.sc-label {
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-tertiary);
}

.sc-meta {
    display: flex;
    align-items: center;
    gap: 8px;
}

.sc-date {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-tertiary);
}

.sc-count {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-disabled);
    background: var(--bg-secondary);
    padding: 2px 8px;
    border-radius: 4px;
}

.sc-toggle {
    display: flex;
    align-items: center;
    color: var(--text-tertiary);
    transition: transform var(--transition-fast);
    margin-left: 2px;
}

.sc-toggle--collapsed {
    transform: rotate(-90deg);
}

.sc-overview {
    font-size: 14px;
    line-height: 1.7;
    color: var(--text-secondary);
    margin: 0 0 16px;
}

.sc-topics {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 16px;
}

.sc-topic {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-body);
    font-size: 12px;
    font-weight: 500;
    padding: 5px 12px;
    border-radius: 20px;
    border: 1px solid var(--border-color);
    background: var(--bg-secondary);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all var(--transition-fast);
}

.sc-topic:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
}

.sc-topic--positive {
    border-color: rgba(34, 197, 94, 0.3);
    background: rgba(34, 197, 94, 0.06);
}
.sc-topic--positive .sc-topic-sentiment {
    color: #22c55e;
}

.sc-topic--neutral {
    border-color: rgba(59, 130, 246, 0.3);
    background: rgba(59, 130, 246, 0.06);
}
.sc-topic--neutral .sc-topic-sentiment {
    color: var(--color-primary);
}

.sc-topic--negative {
    border-color: rgba(239, 68, 68, 0.3);
    background: rgba(239, 68, 68, 0.06);
}
.sc-topic--negative .sc-topic-sentiment {
    color: #ef4444;
}

.sc-topic-sentiment {
    font-family: var(--font-mono);
    font-size: 10px;
    opacity: 0.8;
}

.sc-details {
    display: flex;
    flex-direction: column;
    gap: 10px;
    border-top: 1px solid var(--border-light);
    padding-top: 14px;
}

.sc-topic-detail-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.sc-topic-detail-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
}

.sc-topic-detail-badge {
    font-family: var(--font-mono);
    font-size: 10px;
    padding: 1px 6px;
    border-radius: 4px;
    background: var(--bg-secondary);
    color: var(--text-tertiary);
}

.sc-topic-detail-summary {
    font-size: 13px;
    color: var(--text-tertiary);
    line-height: 1.6;
    margin: 0;
}
</style>
